import { valibotResolver } from '@hookform/resolvers/valibot'
import { useRouter, type NavigateOptions } from '@tanstack/react-router'
import { type RcFile } from 'antd/es/upload'
import { useAtomValue, type PrimitiveAtom } from 'jotai'
import { useAtomCallback } from 'jotai/utils'
import { useCallback, useEffect, type FC, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { FaCheckCircle } from 'react-icons/fa'

import { Button, Modal, ModalBody, ModalFooter } from '@/components/elements'
import { ACTIONS } from '@/constants/common/message.constants'
import { toastApiErrorMessage } from '@/features/shared/utils'
import { FileUploadDragger } from '@/features/uploads/components'
import { type UploadFileResult } from '@/features/uploads/models'
import { validateCsvFile } from '@/utils/upload-file-validator'

import { selectedDcIdAtom } from '../../-states'

import {
  csvImportFormValueSchema,
  type CsvImportFormValue
} from './csv-import-form-value'

type ConfigProps = {
  tenantKey: string
  modalTitle: string
  draggerTitle: string
  draggerDescription: ReactNode
  /** 省略時は CSV ファイルを許可する。エラー時はメッセージを返す */
  isValidFile?: (file: RcFile) => string | null
  /** dcId は選択中の拠点。dto に含めるかは呼び出し側で決める */
  createImportJob: (
    formValue: CsvImportFormValue,
    dcId: number
  ) => Promise<unknown>
  successMessage: string
  errorMessage: string
  /** 取込後に遷移する取込履歴画面のルート */
  historyRoute: NavigateOptions['to']
}

type Props = ConfigProps & {
  /** モーダルの開閉状態を保持する atom */
  isOpenAtom: PrimitiveAtom<boolean>
}

/**
 * CSVインポートModal（マスタ共通）
 *
 * FileUploadDragger + キャンセル/取り込みフッターの骨格を共通化し、
 * 文言・ジョブ作成 API・遷移先を props で受ける。
 * 取込開始成功時は toast を表示して取込履歴画面へ遷移し、
 * 画面遷移の解決を検知してモーダルを閉じる。
 */
export const CsvImportModal: FC<Props> = ({
  isOpenAtom,
  modalTitle,
  ...contentProps
}) => {
  const isOpen = useAtomValue(isOpenAtom)

  const handleCancel = useAtomCallback(
    useCallback(
      (_, set) => {
        set(isOpenAtom, false)
      },
      [isOpenAtom]
    )
  )

  return (
    <Modal title={modalTitle} width={580} open={isOpen} onCancel={handleCancel}>
      <CsvImportModalContent {...contentProps} onCancel={handleCancel} />
    </Modal>
  )
}

type ContentProps = Omit<ConfigProps, 'modalTitle'> & {
  onCancel: () => void
}

/**
 * モーダルの中身。Modal（destroyOnClose 固定）の子として
 * クローズのたびに unmount されることで、フォーム状態のリセットと
 * router.subscribe の解除をライフサイクルとして保証する。
 * useForm やサブスクリプションをこの外へリフトアップしないこと（クローズ後も状態が残り、
 * 再オープン時に前回の fileKey のまま取り込みボタンが活性化してしまう）。
 */
const CsvImportModalContent: FC<ContentProps> = ({
  tenantKey,
  draggerTitle,
  draggerDescription,
  isValidFile = validateCsvFile,
  createImportJob,
  successMessage,
  errorMessage,
  historyRoute,
  onCancel
}) => {
  const router = useRouter()

  const {
    setValue,
    handleSubmit,
    formState: { isSubmitting, isValid }
  } = useForm<CsvImportFormValue>({
    values: {
      fileKey: '',
      fileName: ''
    },
    resolver: valibotResolver(csvImportFormValueSchema)
  })

  const handleChangeUploadFile = useCallback(
    (result: UploadFileResult | null) => {
      setValue('fileKey', result?.key ?? '', { shouldValidate: true })
      setValue('fileName', result?.name ?? '', { shouldValidate: true })
    },
    [setValue]
  )

  const selectedDcId = useAtomValue(selectedDcIdAtom)
  const onSubmit = useCallback(
    async (formValue: CsvImportFormValue) => {
      try {
        await createImportJob(formValue, selectedDcId)
        onCancel()
        toast.success(successMessage)
        router.navigate({
          to: historyRoute,
          params: { tenantKey }
        })
      } catch (error) {
        toastApiErrorMessage(error, errorMessage)
      }
    },
    [
      createImportJob,
      selectedDcId,
      onCancel,
      successMessage,
      router,
      historyRoute,
      tenantKey,
      errorMessage
    ]
  )

  useEffect(() => {
    const unsubscribe = router.subscribe('onResolved', () => {
      onCancel()
    })
    return () => {
      unsubscribe()
    }
  }, [router, onCancel])

  return (
    <form
      onSubmit={handleSubmit(onSubmit, (error) => console.log('error', error))}
    >
      <ModalBody>
        <FileUploadDragger
          title={draggerTitle}
          description={draggerDescription}
          isValidFile={isValidFile}
          onChangeUploadFile={handleChangeUploadFile}
        />
      </ModalBody>
      <ModalFooter>
        <Button onClick={onCancel}>{ACTIONS.CANCEL}</Button>
        <Button
          type="primary"
          icon={<FaCheckCircle />}
          htmlType="submit"
          disabled={!isValid}
          loading={isSubmitting}
        >
          {ACTIONS.IMPORT}
        </Button>
      </ModalFooter>
    </form>
  )
}
